/**
 * Small helpers shared by the landing page's sections.
 */

/**
 * Web-sized sources for a card's art. The originals in /cards are ~1 MB print
 * scans; scripts/optimize-cards.py writes 320px and 640px WebP copies to
 * /cards/web, which is all a card on this page ever needs.
 *
 * @param {string} filename  the card's `filename`, e.g. "the_star.jpg"
 * @param {string} sizes     the rendered width, for the browser to pick from
 */
export const cardImage = (filename, sizes = '(max-width: 560px) 40vw, 220px') => {
    const stem = filename.replace(/\.jpg$/, '')
    return {
        src: `/cards/web/${stem}-320.webp`,
        srcSet: `/cards/web/${stem}-320.webp 320w, /cards/web/${stem}-640.webp 640w`,
        sizes,
    }
}

/**
 * Records a landing-page event if a privacy-friendly analytics script is on
 * the page (Umami or Plausible), and does nothing otherwise. Kept in one
 * place so wiring up analytics later is a single <script> tag in index.html.
 */
export const track = (event, data) => {
    try {
        if (window.umami?.track) window.umami.track(event, data)
        else if (typeof window.plausible === 'function') window.plausible(event, { props: data })
    } catch {
        // Analytics must never break the page
    }
}

// The card a visitor drew in the landing-page teaser, carried through
// sign-up and email confirmation (often a new tab) to their first visit to
// the dashboard. localStorage, because sessionStorage would not survive the
// confirmation link opening in a new tab.
const DRAWN_KEY = 'askarkana.drawnCard'
const DRAWN_TTL = 1000 * 60 * 60 * 24 * 14 // two weeks

export const saveDrawnCard = (cardId) => {
    try {
        localStorage.setItem(DRAWN_KEY, JSON.stringify({ id: cardId, at: Date.now() }))
    } catch {
        // Storage blocked: the hand-off is a nicety, not a requirement
    }
}

/** The id of the card drawn on the landing page, or null. */
export const readDrawnCard = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(DRAWN_KEY))
        if (!saved || typeof saved.id !== 'number') return null
        if (Date.now() - saved.at > DRAWN_TTL) {
            localStorage.removeItem(DRAWN_KEY)
            return null
        }
        return saved.id
    } catch {
        return null
    }
}

export const clearDrawnCard = () => {
    try {
        localStorage.removeItem(DRAWN_KEY)
    } catch {
        // Nothing to clear
    }
}
